/**
 *
 * AppNavigator
 *
 */

import React, { useState, useEffect } from 'react';
import { auth } from '../../firebaseConfig';
import { Popup, Button, Icon } from 'semantic-ui-react';

import styles from './app-navigator.module.css';
import { Link } from 'react-router-dom';
import * as routes from 'constants/routes';
import SubscriptionUtils from '../../utils/SubscriptionUtils';

import FlexBox from 'components/common/FlexBox';
import { FlexNavigator } from 'components/common';
function AppNavigator(props) {
  const { history, app } = props;
  const { user, memberProfile } = app;

  let isLocalhost = location.hostname === 'localhost';

  // Check for admin URL parameter
  const getQueryParams = () => {
    try {
      const query = window.location.search.substring(1);
      const pairs = {};
      const vars = query.split('&');
      for (let i = 0; i < vars.length; i++) {
        const pair = vars[i].split('=');
        pairs[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
      }
      return pairs;
    } catch (e) {
      return {};
    }
  };

  const queryParams = getQueryParams();
  const isUrlAdmin = queryParams.admin === 'true';

  // Click counter state for plan name easter egg
  const [clickCount, setClickCount] = useState(0);
  const [isClickAdmin, setIsClickAdmin] = useState(false);

  // Check localStorage for admin state on component mount
  useEffect(() => {
    const storedAdminState = localStorage.getItem('survcart_admin_mode');
    if (storedAdminState === 'true') {
      setIsClickAdmin(true);
    }
  }, []);

  // Combined admin state (URL param OR click activation)
  const isAdmin = isUrlAdmin || isClickAdmin;

  // Handle plan name clicks
  const handlePlanNameClick = () => {
    if (isClickAdmin) return; // Already admin, no need to count

    const newClickCount = clickCount + 1;
    setClickCount(newClickCount);

    if (newClickCount >= 15) {
      setIsClickAdmin(true);
      localStorage.setItem('survcart_admin_mode', 'true');
      setClickCount(0); // Reset counter

      // Optional: Show some feedback
      console.log('🎉 Admin mode activated!');
    }
  };

  // Double-click to reset admin mode (useful for testing)
  const handlePlanNameDoubleClick = () => {
    if (isClickAdmin) {
      setIsClickAdmin(false);
      localStorage.removeItem('survcart_admin_mode');
      setClickCount(0);
      console.log('Admin mode deactivated');
    }
  };

  let navData = [];
  navData.push({
    label: 'Account',
    route: routes.ACCOUNT,
    icon: 'account_circle',
  });
  navData.push({
    label: 'Dashboard',
    route: routes.DASHBOARD + '/orders',
    icon: 'grid_view',
  });
  navData.push({
    label: 'Inventory',
    route: routes.PRODUCTS,
    icon: 'inventory',
  });
  navData.push({
    label: 'Storefronts',
    route: routes.STOREFRONTS,
    icon: 'storefront',
  });

  const handleNavClick = data => {
    history.push(data.route);
    window.location = data.route;
  };

  const handleSignout = () => {
    auth.signOut();
    props.signOut();
  };
  const clearCache = () => {
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(async name => {
          await caches.delete(name);
        });
      });
    }
    window.location.reload(true);
  };

  let embedded = app.embedded;
  const navElems = navData.map(data => {
    const selected = history.location.pathname.indexOf(data.route) === 0;
    let badge;
    if (data.badge) {
      badge = <div className={styles.appNavBadge}>{data.badge}</div>;
    }
    return (
      <Link
        key={data.route}
        to={data.route}
        className={`${styles.appNavButton} ${
          data.disabled === true ? styles.appNavDisabled : ''
        } ${embedded ? styles.embedded : ''} ${
          selected ? styles.selected : ''
        }`}
      >
        <span className={`${styles.appNavIcon} material-icons`}>
          {data.icon}
        </span>
        {data.label}
        {badge}
      </Link>
    );
  });

  let businessName =
    app.selectedCompany && app.selectedCompany.properties
      ? app.selectedCompany.properties.businessName
      : undefined;
  let planName = SubscriptionUtils.getSubscriptionPlanName(app.selectedCompany);
  let planIcon;
  let planBadgeStyle = [];
  if (planName === 'Basic' || planName === 'Early-Adopter') {
    planBadgeStyle.color = 'rgb(125,125,125)';
    planIcon = 'done';
  } else if (planName === 'Premium') {
    planBadgeStyle.color = 'var(--selection-color)';
    planIcon = 'diamond';
  }

  const switchTarget = () => {
    if (window.localStorage.survcartTargetEnv !== 'prod') {
      window.localStorage.survcartTargetEnv = 'prod';
    } else {
      window.localStorage.survcartTargetEnv = 'dev';
    }

    window.location.reload(true);
  };

  const isDev = window.docketConfig === 'dev';

  let options = [
    {
      type: 'heading',
      text: 'Administration',
    },
    {
      key: 'administration',
      value: 'administration',
      text: 'Settings',
      icon: 'admin_panel_settings',
      material: true,
    },
    {
      key: 'appearance',
      value: 'appearance',
      text: 'Appearance',
      icon: 'palette',
      material: true,
    },
    {
      key: 'notification_management',
      value: 'notification_management',
      text: 'Notifications',
      icon: 'notifications',
      material: true,
    },
    {
      text: 'Core',
      type: 'heading',
    },
    {
      key: 'workflows',
      value: 'workflows',
      text: 'Storefronts',
      icon: 'storefront',
      material: true,
    },
    {
      key: 'products',
      value: 'products',
      text: 'Products',
      icon: 'style',
      material: true,
    },
    {
      key: 'orders',
      value: 'orders',
      text: 'Orders',
      icon: 'receipt_long',
      material: true,
    },
    {
      key: 'promotions',
      value: 'promotions',
      text: 'Promotions',
      icon: 'savings',
      material: true,
    },
    {
      type: 'heading',
      text: 'Insights',
    },
    {
      key: 'sessions',
      value: 'sessions',
      text: 'Sessions',
      icon: 'timeline',
      material: true,
    },
    {
      type: 'heading',
      text: 'Developer',
    },
    {
      key: 'webhooks',
      value: 'webhooks',
      text: 'Webhooks',
      icon: 'webhook',
      visible: false,
      material: true,
    },
    {
      key: 'website',
      value: 'website',
      text: 'Website Scripts',
      icon: 'code',
      material: true,
    },
  ];

  function handleNavigationChange(option) {
    const { value } = option;
    if (value === 'administration') {
      history.push(routes.ACCOUNT + '#connections');
    } else if (value === 'orders') {
      history.push(routes.DASHBOARD + '/orders');
    } else if (value === 'sessions') {
      history.push(routes.DASHBOARD + '/sessions');
    } else if (value === 'workflows') {
      history.push(routes.STOREFRONTS);
    } else if (value === 'appearance') {
      history.push(routes.STOREFRONTS + '/appearance');
    } else if (value === 'website') {
      history.push(routes.STOREFRONTS + '/scripting');
    } else if (value === 'webhooks') {
      history.push(routes.WEBHOOKS);
    } else if (value === 'products') {
      history.push(routes.PRODUCTS);
    } else if (value === 'promotions') {
      history.push(routes.PRODUCTS + '/promotions');
    } else if (value === 'notification_management') {
      history.push(routes.DASHBOARD + '/sessions/notification-management');
    }
  }

  return (
    <FlexBox
      column
      vstretch
      leftGutter
      rightGutter
      align="center"
      style={{
        minWidth: 150,
        borderRight: '1px solid var(--background-accent-color)',
      }}
    >
      <FlexBox vstretch hstretch column align="center">
        <FlexNavigator
          defaultValue="workflows"
          options={options}
          onChange={handleNavigationChange}
          vertical
        />
      </FlexBox>
      {!embedded && (
        <div className={styles.profileButton}>
          <img className={styles.profilePicture} src={user.profilePic} />
          <div className={styles.profileTextContent}>
            <div className={styles.userName}>
              {user.first} {user.last}
            </div>
            <div className={styles.businessName}>{businessName}</div>
            <div style={planBadgeStyle} className={styles.planBadge}>
              <div className="material-icons">{planIcon}</div>
              <span
                onClick={handlePlanNameClick}
                onDoubleClick={handlePlanNameDoubleClick}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title={
                  !isAdmin && clickCount > 0 ? `${clickCount}/15 clicks` : ''
                }
              >
                {planName}
              </span>
              {isAdmin ? (
                <div className={`${styles.targetTag} ${styles.targetAdmin}`}>
                  ADMIN
                </div>
              ) : (
                isLocalhost && (
                  <div
                    className={`${styles.targetTag} ${
                      isDev ? styles.targetDev : styles.targetProd
                    }`}
                    onClick={switchTarget}
                  >
                    {isDev ? 'DEV' : 'PROD'}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
      {embedded && (
        <div style={planBadgeStyle} className={styles.planBadge}>
          <div className="material-icons">{planIcon}</div>
          <span
            onClick={handlePlanNameClick}
            onDoubleClick={handlePlanNameDoubleClick}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            title={!isAdmin && clickCount > 0 ? `${clickCount}/15 clicks` : ''}
          >
            {planName}
          </span>
          {isAdmin ? (
            <div className={`${styles.targetTag} ${styles.targetAdmin}`}>
              ADMIN
            </div>
          ) : (
            isLocalhost && (
              <div
                className={`${styles.targetTag} ${
                  isDev ? styles.targetDev : styles.targetProd
                }`}
                onClick={switchTarget}
              >
                {isDev ? 'DEV' : 'PROD'}
              </div>
            )
          )}
        </div>
      )}
    </FlexBox>
  );
}

AppNavigator.propTypes = {};

export default AppNavigator;
